/**
 * Link para uma âncora da home (#inicio, #novas, #usadas, #sobre).
 *
 * O protótipo era uma página só, então `href="#novas"` bastava. Agora existem
 * `/bicicletas` e `/bicicletas/:slug`, e um `#novas` clicado a partir dessas
 * rotas não acharia a seção. Este componente resolve os dois casos:
 *  - já está em `/`  → rola suavemente até a seção;
 *  - está em outra rota → navega para `/` e rola depois da montagem.
 */
import { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import type { ReactNode } from 'react'

export type Ancora = 'inicio' | 'novas' | 'usadas' | 'sobre'

export function LinkAncora({
  ancora,
  children,
  className,
  aoNavegar,
}: {
  ancora: Ancora
  children: ReactNode
  className?: string
  /** Usado pelo menu mobile para fechar o painel ao clicar. */
  aoNavegar?: () => void
}) {
  const localizacao = useLocation()
  const navegar = useNavigate()
  const naHome = localizacao.pathname === '/'

  const aoClicar = useCallback(
    (evento: React.MouseEvent<HTMLAnchorElement>) => {
      // Deixa o navegador cuidar de ctrl/cmd+clique e clique do meio.
      if (evento.metaKey || evento.ctrlKey || evento.shiftKey || evento.button !== 0) return

      evento.preventDefault()
      aoNavegar?.()

      if (naHome) {
        document.getElementById(ancora)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        // Mantém a URL sincronizada sem empilhar histórico a cada clique.
        window.history.replaceState(null, '', `/#${ancora}`)
        return
      }

      navegar(`/#${ancora}`)
    },
    [ancora, aoNavegar, naHome, navegar],
  )

  return (
    <a href={`/#${ancora}`} onClick={aoClicar} className={className}>
      {children}
    </a>
  )
}
